import cn from "classnames"
import React, { useEffect, useRef, useState } from "react"
import { ChevronLeftLarge, ChevronRightLarge } from "@/skylticons"

export interface ScrollListProps {
  hideButtons?: boolean
  noHover?: boolean
  buttonClassName?: string
  buttonsOutside?: boolean
  className?: string
  gap?: number
  moreButtonGtmTag?: string
}

const ScrollList: React.FC<ScrollListProps & { children: React.ReactNode }> = ({
  children,
  className,
  gap: itemGap,
  hideButtons,
  noHover,
  buttonClassName,
  buttonsOutside,
  moreButtonGtmTag,
}) => {
  const listRef = useRef<HTMLDivElement>(null)
  const [, setUpdate] = useState(0)
  const list = listRef.current
  const gap = 16

  const recalc = () => setUpdate(u => u + 1)

  let scrolledToStart = false
  let scrolledToEnd = false
  let progressLeft = 0
  let progressWidth = 0

  let next: () => void = () => null
  let previous: () => void = () => null

  if (list) {
    scrolledToStart = list.scrollLeft === 0
    scrolledToEnd = list.scrollLeft === list.scrollWidth - list.clientWidth

    const listChildren = Array.from(list.children) as HTMLElement[]
    // left and right X of currently visible area
    const visibleRange = [list.scrollLeft, list.scrollLeft + list.clientWidth]

    progressWidth = (list.clientWidth / list.scrollWidth) * 100
    progressLeft = (list.scrollLeft / list.scrollWidth) * 100

    // indexes of visible children
    const visible = listChildren
      .filter(child => {
        const childLeft = child.offsetLeft
        const childRight = childLeft + child.clientWidth
        return childLeft >= visibleRange[0] && childRight <= visibleRange[1]
      })
      .map(child => listChildren.indexOf(child))

    // scroll to right edge of previous not fully visible item
    previous = () => {
      const prevItem = listChildren[Math.max(visible[0] - 1, 0)]
      const left = prevItem.offsetLeft + prevItem.clientWidth + gap - list.clientWidth
      list.scrollTo({ left, behavior: "smooth" })
    }

    // scroll to left edge of next not fully visible item
    next = () => {
      const nextItem = listChildren[Math.min(visible[visible.length - 1] + 1, listChildren.length - 1)]
      const left = nextItem.offsetLeft - gap
      list.scrollTo({ left, behavior: "smooth" })
    }
  }

  // update calculations on scroll
  useEffect(() => {
    const handler = () => {
      recalc()
    }

    list?.addEventListener("scroll", handler)
    return () => list?.removeEventListener("scroll", handler)
  }, [list])

  // reset scroll on resize
  useEffect(() => {
    const handler = () => {
      if (list) list.scrollLeft = 0
      recalc()
    }

    const observer = new ResizeObserver(handler)

    if (list) {
      observer.observe(list)
    }

    return () => observer.disconnect()
  }, [list])

  // update when children change
  useEffect(() => {
    recalc()
  }, [children])

  return (
    <>
      <div className={cn("relative max-w-[100vw]", { "group/scrollList": !noHover })}>
        <div className="scrollbar-hide overflow-y-hidden overflow-x-scroll lg:max-w-none lg:overflow-x-hidden">
          <div
            ref={listRef}
            className={cn(
              "container-scroll lg:container-no-scroll scrollbar-hide flex overflow-y-hidden lg:overflow-x-scroll",
              className
            )}
            style={{ gap: itemGap || 16 }}
          >
            {children}
          </div>
        </div>
        {list && !hideButtons && (
          <div className="hidden onlyHover:block">
            {!scrolledToStart && (
              <button
                className={cn(
                  "button button-secondary button-medium icon-only raised absolute -translate-y-5",
                  {
                    "onlyHover:opacity-50 group-hover/scrollList:onlyHover:opacity-100": !noHover,
                    "left-2": !buttonsOutside,
                    "left-0 -translate-x-1/2": buttonsOutside,
                  },
                  buttonClassName
                )}
                type="button"
                onClick={previous}
              >
                <ChevronLeftLarge className="h-10 w-10" />
              </button>
            )}
            {!scrolledToEnd && (
              <button
                className={cn(
                  "button button-secondary button-medium icon-only raised absolute -translate-y-5",
                  {
                    "onlyHover:opacity-50 group-hover/scrollList:onlyHover:opacity-100": !noHover,
                    "right-2": !buttonsOutside,
                    "right-0 translate-x-1/2": buttonsOutside,
                  },
                  buttonClassName
                )}
                type="button"
                onClick={next}
                data-gtm={moreButtonGtmTag}
              >
                <ChevronRightLarge className="h-10 w-10" />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="container mt-2 py-3 lg:onlyHover:hidden">
        <div className="relative h-0.5 overflow-hidden rounded-full bg-white-20">
          <div
            className="absolute top-0 h-0.5 overflow-hidden rounded-full bg-ink-100"
            style={{ left: `${progressLeft || 0}%`, width: `${progressWidth || 0}%` }}
          ></div>
        </div>
      </div>
    </>
  )
}

export default ScrollList
